import type { FC } from '../../lib/teact/teact';
import React, {
  memo, useCallback, useEffect, useRef, useState,
} from '../../lib/teact/teact';
import { withGlobal } from '../../global';

import type { ApiCountryCode } from '../../api/types';

import { getAuthCopy } from '../../util/authI18n';
import buildClassName from '../../util/buildClassName';
import { isoToEmoji } from '../../util/emoji/emoji';
import { prepareSearchWordsForNeedle } from '../../util/searchWords';
import renderText from '../common/helpers/renderText';

import useLang from '../../hooks/useLang';
import useSyncEffect from '../../hooks/useSyncEffect';

import MenuItem from '../ui/MenuItem';
import Spinner from '../ui/Spinner';

import './CountryCodeInput.scss';

type StateProps = {
  phoneCodeList: ApiCountryCode[];
};

type OwnProps = {
  id: string;
  value?: ApiCountryCode;
  isLoading?: boolean;
  onChange: (value: ApiCountryCode) => void;
};

const CountryCodeInput: FC<OwnProps & StateProps> = ({
  id,
  value,
  isLoading,
  onChange,
  phoneCodeList,
}) => {
  const lang = useLang();
  const authCopy = getAuthCopy();
  // eslint-disable-next-line no-null/no-null
  const inputRef = useRef<HTMLInputElement>(null);
  // eslint-disable-next-line no-null/no-null
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const [filter, setFilter] = useState<string | undefined>();
  const [filteredList, setFilteredList] = useState<ApiCountryCode[]>([]);

  const updateFilter = useCallback((filterValue?: string) => {
    setFilter(filterValue);
    setFilteredList(getFilteredList(phoneCodeList, filterValue));
  }, [phoneCodeList]);

  useSyncEffect(([prevPhoneCodeList]) => {
    if (!prevPhoneCodeList?.length && phoneCodeList.length) {
      setFilteredList(getFilteredList(phoneCodeList, filter));
    }
  }, [phoneCodeList, filter]);

  const handleChange = useCallback((country: ApiCountryCode) => {
    onChange(country);
    updateFilter(undefined);
    setIsOpen(false);
  }, [onChange, updateFilter]);

  const handleInput = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    updateFilter(e.currentTarget.value);
  }, [updateFilter]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    updateFilter(undefined);
  }, [updateFilter]);

  const handleOverlayClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.keyCode === 27) {
      handleClose();
    }
  }, [handleClose]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    searchInputRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  return (
    <div className="CountryCodeInput">
      <button
        type="button"
        id={id}
        ref={inputRef as React.RefObject<HTMLButtonElement>}
        className={buildClassName('country-code-trigger', isOpen && 'open')}
        onClick={handleOpen}
      >
        <span className="country-code-trigger-flag">
          {value ? renderText(isoToEmoji(value.iso2), ['hq_emoji']) : '🌐'}
        </span>
        <span className="country-code-trigger-value">+{value?.countryCode || '--'}</span>
        {isLoading ? <Spinner color="black" /> : <span className="country-code-trigger-arrow">▼</span>}
      </button>

      {isOpen && (
        <div className="country-picker-overlay" onClick={handleOverlayClick}>
          <div className="country-picker-panel">
            <div className="country-picker-header">
              <div className="country-picker-title">{authCopy.countryPickerTitle}</div>
              <button type="button" className="country-picker-close" onClick={handleClose}>×</button>
            </div>
            <div className="country-picker-search">
              <span className="country-picker-search-icon">🔎</span>
              <input
                ref={searchInputRef}
                type="text"
                className="country-picker-search-input"
                placeholder={authCopy.countryPickerSearchPlaceholder}
                value={filter || ''}
                autoComplete="off"
                onInput={handleInput}
                onKeyDown={handleSearchKeyDown}
              />
            </div>
            <div className="country-picker-list custom-scroll">
              {filteredList.map((country: ApiCountryCode) => (
                <MenuItem
                  key={`${country.iso2}-${country.countryCode}`}
                  className={buildClassName('country-picker-item', value && country.iso2 === value.iso2 && 'selected')}
                  onClick={() => handleChange(country)}
                >
                  <span className="country-flag">{renderText(isoToEmoji(country.iso2), ['hq_emoji'])}</span>
                  <span className="country-name-wrap">
                    <span className="country-name">{country.name || country.defaultName}</span>
                    {country.name && country.defaultName && country.name !== country.defaultName && (
                      <span className="country-default-name">{country.defaultName}</span>
                    )}
                  </span>
                  <span className="country-code">+{country.countryCode}</span>
                </MenuItem>
              ))}
              {!filteredList.length && (
                <MenuItem
                  key="no-results"
                  className="country-picker-item no-results"
                  disabled
                >
                  <span>{lang('CountryNone')}</span>
                </MenuItem>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function getFilteredList(countryList: ApiCountryCode[], filter = ''): ApiCountryCode[] {
  if (!filter.length) {
    return countryList;
  }

  const normalizedFilter = filter.trim();
  if (!normalizedFilter) {
    return countryList;
  }

  const digitsOnlyFilter = normalizedFilter.replace(/[^\d]+/g, '');
  const textOnlyFilter = normalizedFilter.replace(/[^\p{L}\p{M}\s]+/gu, ' ').trim();
  const searchWords = textOnlyFilter ? prepareSearchWordsForNeedle(textOnlyFilter) : undefined;

  return countryList.filter((country) => {
    const matchesName = Boolean(
      searchWords && (searchWords(country.defaultName) || (country.name && searchWords(country.name))),
    );
    const matchesIso = Boolean(searchWords && searchWords(country.iso2));
    const matchesCode = (
      normalizedFilter === `+${country.countryCode}`
      || normalizedFilter === country.countryCode
      || (digitsOnlyFilter ? country.countryCode.includes(digitsOnlyFilter) : false)
    );

    return matchesName || matchesIso || matchesCode;
  });
}

export default memo(withGlobal<OwnProps>(
  (global): StateProps => {
    const { countryList: { phoneCodes: phoneCodeList } } = global;
    return {
      phoneCodeList,
    };
  },
)(CountryCodeInput));
